import {Button, Input} from "antd";
import SimpleCard from "@/base/components/dashboard/SimpleCart";
import React, {useEffect} from "react";
import {useUser} from "@/base/components/auth/UserProvider";
import {useTranslation} from "next-i18next";
import {getRefValue} from "@/base/utils/form";
import {ModifyURL} from "@/base/utils/fetch";
import {useNotification} from "@/base/components/NotificationProvider";
import {hasCache, putCache, withCache} from "@/base/utils/cache";
import {useRouter} from "next/router";

type Props = {
	children?: React.ReactNode;
	className?: string;
}

const ignoredPaths: string[] = [
	'/dashboard/promo',
	'/dashboard/topup',
	'/dashboard/referral',
	'/dashboard/package/[id]',
];

export const EmailConfirm = ({
	children = null,
	className = ''
}: Props) => {
	const {t} = useTranslation();
	const UserContext = useUser();
	const [api] = useNotification();
	const router = useRouter();

	const inputRef = React.useRef(null);

	const [hide, setHide] = React.useState(false);

	const sendList = async () => {
		const email = getRefValue(inputRef);

		if (email != UserContext.user?.email)
		{
			await UserContext.Fetch({
				url: ModifyURL(`/user/settings`),
				method: 'PUT',
				body: {
					email
				}
			}).then(res => {
				UserContext.updateUser();
			}).catch(err => {
				api.error({
					message: err.message || err
				});
			});
		}

		return UserContext.Fetch({
			url: ModifyURL(`/user/resend-email`),
			method: 'PUT',
			body: {
				redirect_url: window.location.href
			}
		}).then(async res => {
			api.success({
				message: t('base-common:email-confirm.sent')
			});

			setHide(true);
			await putCache('email_send_was', email || UserContext.user?.email || '');
		}).catch(err => {
			api.error({
				message: err.message || err
			});
		});
	}

	useEffect(() => {
		if (hasCache('email_send_was'))
		{
			withCache('email_send_was', 1000 * 60 * 5, async () => '').then(email => {
				if (email)
				{
					setHide(true);
				}
			});
		}
	}, []);

	const isPathIgnored = ignoredPaths.includes(router.pathname);

	return !isPathIgnored && (
		<SimpleCard
			headerClassName={`bg-red-100 dark:bg-red-600/10 rounded-t-xl`}
			className={`${className} ${hide && 'hidden'}`}
			bodyClassName={`lg:py-2 bg-red-50 dark:bg-red-400/10 rounded-b-xl`}
			title={t(`base-common:email-confirm.title`)}
		>
			<p>
				{t(`base-common:email-confirm.description`)}
			</p>

			<div className={`md:max-w-[300px]`}>
				<div className={`mt-4`}>
					<p className={`text-sm mb-1`}>
						{t('base-common:email-confirm.email-title')}
					</p>
					<Input
						type="email"
						defaultValue={UserContext.user?.email || ''}
						className={`sp-input-clear`}
						placeholder={`a-user@stableproxy.com`}
						ref={inputRef}
					/>
				</div>

				<div className={`mt-4`}>
					<Button
						type="primary"
						className={`w-full !shadow-none`}
						onClick={sendList}
					>
						{t('base-common:email-confirm.send')}
					</Button>
				</div>
			</div>

			{children}
		</SimpleCard>
	);
}